import React, {
	CSSProperties,
	FC,
	Fragment,
	ReactNode,
	useEffect,
	useRef,
	useState,
	useCallback,
} from 'react';
import { TipContent } from './TipContent';
import { noopFunc } from '@shared/utils/function';
import { useClickAway } from 'ahooks';
import { isObject } from 'lodash-es';
import { useComposeRef } from './utils/useComposeRef';

interface TooltipProps {
	children: ReactNode;
	content: ReactNode; // tip的内容
	triggerWay?: 'hover' | 'click'; // 触发方式
	placement?: 'top' | 'right';
	overlayStyle?: CSSProperties; // tooltip样式
	overlayClassName?: string; // tooltip类名
	visible?: boolean; // 是否可见
	onVisibleChange?: (visible: boolean) => void; // visible修改时的回调
	delay?: number; // 出现延时 单位ms
	[key: string]: any; // rest 参数
}

export const Tooltip: FC<TooltipProps> = (props) => {
	const {
		children,
		content,
		triggerWay = 'hover',
		placement = 'top',
		overlayClassName = '',
		overlayStyle = {},
		visible,
		delay,
		onVisibleChange = noopFunc,
		...rest
	} = props;

	const [expand, setExpand] = useState(Boolean(visible)); // 是否展开

	const eleRef = useRef<HTMLElement>(null);

	const times = useRef<number>(0); // 点击次数

	const expandCb = useCallback(() => {
		if (visible === undefined) {
			setExpand(true);
			onVisibleChange(true);
		}
	}, [onVisibleChange, visible]);

	const shrinkCb = useCallback(() => {
		if (visible === undefined) {
			setExpand(false);
			onVisibleChange(false);
		}
	}, [onVisibleChange, visible]);

	const toggleCb = useCallback(() => {
		if (visible !== undefined) {
			return;
		}
		times.current += 1;
		if (times.current === 1) {
			const timer = setTimeout(() => {
				if (times.current === 1) {
					setExpand((ep) => {
						onVisibleChange(!ep);
						return !ep;
					});
				}
				times.current = 0;
				clearTimeout(timer);
			}, 200);
		}
	}, [onVisibleChange, visible]);

	useEffect(() => {
		if (visible !== undefined) {
			setExpand(visible);
			visible !== expand && onVisibleChange(visible);
		}
	}, [visible, onVisibleChange, expand]);

	useEffect(() => {
		if (eleRef.current) {
			if (triggerWay === 'hover') {
				eleRef.current.addEventListener('mouseenter', expandCb);
				eleRef.current.addEventListener('mouseleave', shrinkCb);
			} else {
				eleRef.current.addEventListener('click', toggleCb);
			}
			return () => {
				if (triggerWay === 'hover') {
					eleRef.current?.removeEventListener('mouseenter', expandCb);
					eleRef.current?.removeEventListener('mouseleave', shrinkCb);
				} else {
					eleRef.current?.removeEventListener('click', toggleCb);
				}
			};
		}
	}, [triggerWay]);

	useClickAway(() => {
		if (visible === undefined) {
			setExpand(false);
		}
	}, [eleRef]);

	let childRef: React.Ref<any> = null;

	if (children && isObject((children as any).ref)) {
		childRef = (children as any).ref;
	}

	const mergedRef = useComposeRef(childRef, eleRef);

	return (
		<Fragment>
			{React.Children.map(children, (child: any) => {
				return React.cloneElement(child, {
					ref: mergedRef,
					className: child?.props?.className,
					id: child?.props?.id,
				});
			})}
			{expand && content && (
				<TipContent
					placement={placement}
					triggerEleRef={eleRef}
					classNames={overlayClassName}
					style={overlayStyle}
					expand={expand}
					delayMs={delay}
					{...rest}
				>
					{content}
				</TipContent>
			)}
		</Fragment>
	);
};
